import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeftRight, FolderTree, LayoutDashboard, ListTree, Plus, Repeat, TrendingUp } from 'lucide-react';
import { Heading } from '../../layout';
import { PillTabBar } from '../../reusable';
import { useUIColors } from '../../../hooks/useTheme';
import { useAuth } from '../../../contexts/AuthContext';
import { hasSectionAction } from '../../../utils/permissions';
import { periodLabelStyle, periodSelectStyle } from '../../placeholders/periodSummaryUtils';
import { getChartAccounts } from '../../../services/treasuryAPI';
import {
  createBudget,
  deleteBudget,
  getBudget,
  getBudgetAccounts,
  getBudgets,
  setBudgetStatus,
} from '../../../services/budgetAPI';
import { buildFormStyles, Pill, TreasuryDrawer } from './treasury/treasuryUi';
import BudgetOverview from './budget/BudgetOverview';
import BudgetHorizonSelector from './budget/BudgetHorizonSelector';
import BudgetAccountsPanel from './budget/BudgetAccountsPanel';
import BudgetIncomeSection from './budget/BudgetIncomeSection';
import BudgetCostsSection from './budget/BudgetCostsSection';
import BudgetLinesPanel from './budget/BudgetLinesPanel';
import BudgetReclassifyPanel from './budget/BudgetReclassifyPanel';

const NAV_ITEMS = [
  { key: 'resumen', label: 'Resumen', icon: LayoutDashboard },
  { key: 'accounts', label: 'Cuentas', icon: FolderTree },
  { key: 'income', label: 'Estructura de ingresos', icon: TrendingUp },
  { key: 'costs', label: 'Estructura de costos', icon: Repeat },
  { key: 'lines', label: 'Líneas', icon: ListTree },
  { key: 'reclassify', label: 'Reclasificaciones', icon: ArrowLeftRight },
];

const STATUS_TONE = {
  Vigente: 'success',
  Borrador: 'info',
  Cerrado: 'warning',
};

const currentYear = () => new Date().getFullYear();

const FinanceBudgetWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const styles = buildFormStyles(ui);
  const { user, hasPermission } = useAuth();
  const canCreate =
    hasSectionAction(user, '/backoffice/finance/budget', 'create') || hasPermission('finance.write');
  const canEdit =
    hasSectionAction(user, '/backoffice/finance/budget', 'update') || hasPermission('finance.write');
  const [section, setSection] = useState('resumen');
  const [year, setYear] = useState(currentYear());
  const [viewMode, setViewMode] = useState('ytd');
  const [budgets, setBudgets] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [chartAccounts, setChartAccounts] = useState([]);
  const [budgetAccounts, setBudgetAccounts] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ year: String(currentYear()), name: '', copy_from_id: '' });
  const [saving, setSaving] = useState(false);

  const throughMonth = useMemo(() => {
    if (viewMode !== 'ytd') return 12;
    const now = new Date();
    return Number(year) === now.getFullYear() ? now.getMonth() + 1 : 12;
  }, [viewMode, year]);

  const yearOptions = useMemo(() => {
    const years = new Set(budgets.map((row) => Number(row.year)));
    years.add(currentYear());
    years.add(currentYear() + 1);
    years.add(Number(year));
    return Array.from(years).filter(Boolean).sort((a, b) => b - a);
  }, [budgets, year]);

  const budgetsInYear = useMemo(
    () => (budgets || []).filter((row) => Number(row.year) === Number(year)),
    [budgets, year]
  );

  const loadList = useCallback(async () => {
    try {
      setError('');
      const payload = await getBudgets();
      setBudgets(Array.isArray(payload) ? payload : []);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar los presupuestos.');
      setBudgets([]);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const payload = await getChartAccounts();
        if (!cancelled) setChartAccounts(Array.isArray(payload) ? payload : []);
      } catch {
        if (!cancelled) setChartAccounts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadAccounts = useCallback(async () => {
    try {
      const payload = await getBudgetAccounts();
      setBudgetAccounts(Array.isArray(payload?.tree) ? payload.tree : []);
    } catch {
      setBudgetAccounts([]);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const handleAccountsChanged = async () => {
    await loadAccounts();
    if (!selectedId) return;
    try {
      const payload = await getBudget(selectedId, { through_month: throughMonth });
      setDetail(payload);
    } catch {
      /* el árbol de cuentas ya se actualizó */
    }
  };

  useEffect(() => {
    if (!budgetsInYear.length) {
      setSelectedId(null);
      setDetail(null);
      return;
    }
    const stillThere = budgetsInYear.some((row) => row.id === selectedId);
    if (stillThere) return;
    const vigente = budgetsInYear.find((row) => row.status === 'Vigente');
    setSelectedId((vigente || budgetsInYear[0]).id);
  }, [budgetsInYear, selectedId]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingDetail(true);
        setError('');
        const payload = await getBudget(selectedId, { through_month: throughMonth });
        if (!cancelled) setDetail(payload);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'No se pudo cargar el presupuesto.');
          setDetail(null);
        }
      } finally {
        if (!cancelled) setLoadingDetail(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId, throughMonth]);

  const handleCreated = (payload) => {
    setDetail(payload);
    setSelectedId(payload.id);
    setYear(Number(payload.year));
    loadList();
  };

  const handleCreate = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = await createBudget({
        year: Number(createForm.year),
        name: createForm.name,
        copy_from_id: createForm.copy_from_id ? Number(createForm.copy_from_id) : null,
      });
      setShowCreate(false);
      setCreateForm({ year: String(currentYear()), name: '', copy_from_id: '' });
      handleCreated(payload);
    } catch (err) {
      setError(err.message || 'No se pudo crear el presupuesto.');
    } finally {
      setSaving(false);
    }
  };

  const handleStatus = async (status) => {
    if (!detail?.id) return;
    setSaving(true);
    setError('');
    try {
      const payload = await setBudgetStatus(detail.id, status);
      setDetail(payload);
      loadList();
    } catch (err) {
      setError(err.message || 'No se pudo cambiar el estado.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!detail?.id || !window.confirm('¿Eliminar este presupuesto?')) return;
    setSaving(true);
    setError('');
    try {
      await deleteBudget(detail.id);
      setSelectedId(null);
      setDetail(null);
      loadList();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar el presupuesto.');
    } finally {
      setSaving(false);
    }
  };

  const locked = detail?.status === 'Cerrado';
  const periodTitle = viewMode === 'ytd' ? `YTD · hasta ${throughMonth === 12 ? 'diciembre' : `mes ${throughMonth}`}` : 'Año completo';

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          alignItems: 'center',
          columnGap: 16,
          rowGap: 12,
          marginBottom: 16,
        }}
      >
        <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Heading level={2} isDarkMode={isDarkMode}>
            Presupuesto
          </Heading>
          {detail ? (
            <Pill label={detail.status} tone={STATUS_TONE[detail.status] || 'info'} isDarkMode={isDarkMode} />
          ) : null}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={periodLabelStyle(ui)}>Año</span>
            <select value={String(year)} onChange={(event) => setYear(Number(event.target.value))} style={periodSelectStyle(ui)}>
              {yearOptions.map((value) => (
                <option key={value} value={String(value)}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={periodLabelStyle(ui)}>Presupuesto</span>
            <select
              value={selectedId || ''}
              onChange={(event) => setSelectedId(Number(event.target.value) || null)}
              style={{ ...periodSelectStyle(ui), minWidth: 180 }}
            >
              {!budgetsInYear.length ? <option value="">Sin presupuesto</option> : null}
              {budgetsInYear.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.budget_code} · {row.status}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={periodLabelStyle(ui)}>Vista</span>
            <select value={viewMode} onChange={(event) => setViewMode(event.target.value)} style={periodSelectStyle(ui)}>
              <option value="ytd">Año en curso</option>
              <option value="year">Año completo</option>
            </select>
          </label>
          {canCreate ? (
            <button type="button" style={styles.primaryBtn} onClick={() => setShowCreate(true)}>
              <Plus size={14} /> Nuevo
            </button>
          ) : null}
          {canEdit && detail?.status === 'Borrador' ? (
            <button type="button" style={styles.primaryBtn} onClick={() => handleStatus('Vigente')} disabled={saving}>
              Activar
            </button>
          ) : null}
          {canEdit && detail?.status === 'Vigente' ? (
            <button type="button" style={styles.ghostBtn} onClick={() => handleStatus('Cerrado')} disabled={saving}>
              Cerrar
            </button>
          ) : null}
          {canEdit && detail && detail.status !== 'Vigente' ? (
            <button type="button" style={styles.ghostBtn} onClick={handleDelete} disabled={saving}>
              Eliminar
            </button>
          ) : null}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 16,
          flexWrap: 'wrap',
        }}
      >
        <PillTabBar ariaLabel="Secciones de presupuesto" items={NAV_ITEMS} activeKey={section} onChange={setSection} />
        <BudgetHorizonSelector
          ui={ui}
          budget={detail}
          canEdit={canEdit && !locked}
          onChanged={setDetail}
        />
      </div>

      {error ? (
        <p style={{ margin: '0 0 12px', color: ui.danger, fontSize: 13 }}>{error}</p>
      ) : null}

      {section === 'accounts' ? (
        <BudgetAccountsPanel
          ui={ui}
          isDarkMode={isDarkMode}
          tree={budgetAccounts}
          chartAccounts={chartAccounts}
          canEdit={canEdit}
          onChanged={handleAccountsChanged}
        />
      ) : null}

      {section !== 'accounts' && !loadingList && !budgetsInYear.length ? (
        <div style={{ background: ui.panel, border: `1px solid ${ui.border}`, borderRadius: 10, padding: 24 }}>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: ui.text }}>No hay presupuesto para {year}</p>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: ui.muted }}>
            Crea un presupuesto anual por cuenta contable y compáralo con el real del libro mayor.
          </p>
        </div>
      ) : null}

      {section !== 'accounts' && detail ? (
        <>
          {section === 'resumen' ? (
            <BudgetOverview
              budget={detail}
              periodTitle={`${detail.name || detail.budget_code} · ${periodTitle}`}
              isDarkMode={isDarkMode}
              ui={ui}
              loading={loadingDetail}
              canEdit={canEdit && !locked}
              onChanged={setDetail}
            />
          ) : null}
          {section === 'income' ? (
            <BudgetIncomeSection
              ui={ui}
              isDarkMode={isDarkMode}
              budget={detail}
              budgetAccounts={budgetAccounts}
              canEdit={canEdit && !locked}
              onChanged={setDetail}
            />
          ) : null}
          {section === 'costs' ? (
            <BudgetCostsSection
              ui={ui}
              isDarkMode={isDarkMode}
              budget={detail}
              chartAccounts={chartAccounts}
              budgetAccounts={budgetAccounts}
              canEdit={canEdit && !locked}
              onChanged={setDetail}
            />
          ) : null}
          {section === 'lines' ? (
            <BudgetLinesPanel
              ui={ui}
              isDarkMode={isDarkMode}
              budget={detail}
              chartAccounts={chartAccounts}
              budgetAccounts={budgetAccounts}
              canEdit={canEdit && !locked}
              onChanged={setDetail}
            />
          ) : null}
          {section === 'reclassify' ? (
            <BudgetReclassifyPanel ui={ui} budget={detail} canEdit={canEdit && !locked} onChanged={setDetail} />
          ) : null}
        </>
      ) : section !== 'accounts' && loadingDetail ? (
        <p style={{ margin: 0, color: ui.muted, fontSize: 13 }}>Cargando presupuesto…</p>
      ) : null}

      {showCreate ? (
        <TreasuryDrawer
          ui={ui}
          title="Nuevo presupuesto"
          subtitle="Un presupuesto vigente por año. Puedes copiar las líneas de otro."
          error={error}
          saving={saving}
          onClose={() => setShowCreate(false)}
          onSave={handleCreate}
          saveLabel="Crear"
        >
          <label style={styles.label}>
            Año
            <input
              value={createForm.year}
              onChange={(event) => setCreateForm((prev) => ({ ...prev, year: event.target.value }))}
              style={styles.input}
            />
          </label>
          <label style={styles.label}>
            Nombre
            <input
              value={createForm.name}
              onChange={(event) => setCreateForm((prev) => ({ ...prev, name: event.target.value }))}
              placeholder={`Presupuesto ${createForm.year}`}
              style={styles.input}
            />
          </label>
          <label style={styles.label}>
            Copiar líneas desde
            <select
              value={createForm.copy_from_id}
              onChange={(event) => setCreateForm((prev) => ({ ...prev, copy_from_id: event.target.value }))}
              style={styles.input}
            >
              <option value="">Partir en blanco</option>
              {budgets.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.year} · {row.budget_code} · {row.status}
                </option>
              ))}
            </select>
          </label>
        </TreasuryDrawer>
      ) : null}
    </div>
  );
};

export default FinanceBudgetWorkspace;
